import { useQuery } from "@tanstack/react-query"
import type { MetricDomain } from "@csbi/metric-catalog"
import { catalog } from "@csbi/metric-catalog"
import { fetchCoverage } from "../tiles/api.js"

/**
 * The coverage matrix. It feeds two places at once: the `/ops/coverage` page, and **the badge on every domain in the sidebar**.
 *
 * That `5/7` in the sidebar was the progress bar during implementation — which domain is not wired up yet is
 * visible without opening it. The whole menu shares this one request.
 */
export function useCoverage() {
  return useQuery({
    queryKey: ["coverage"],
    queryFn: ({ signal }) => fetchCoverage(signal),
    staleTime: 5 * 60_000,
  })
}

export type DomainCoverage = { linked: number; total: number }

const TOTALS = new Map<MetricDomain, number>()
for (const m of catalog) TOTALS.set(m.domain, (TOTALS.get(m.domain) ?? 0) + 1)

/**
 * Per domain, "metrics whose pipeline works / total".
 *
 * "Works" = at least one deployment is `ok`, the same definition as `metricsLinked`.
 * The denominator comes from the local catalog rather than the number of entries in the response: when the API
 * is down it still shows `0/7` rather than a blank: "not wired up yet" and "unknown" are two different things on screen.
 */
export function domainCoverage(
  matrix: { metrics: readonly { domain: string; cells: readonly string[] }[] } | undefined,
): Map<MetricDomain, DomainCoverage> {
  const out = new Map<MetricDomain, DomainCoverage>()
  for (const [domain, total] of TOTALS) out.set(domain, { linked: 0, total })
  if (!matrix) return out

  for (const m of matrix.metrics) {
    const entry = out.get(m.domain as MetricDomain)
    if (entry && m.cells.some((c) => c === "ok")) entry.linked += 1
  }
  return out
}
